import {
  useEffect,
  useId,
  useRef,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from 'react';

import { cx } from './classNames';
import { IconButton } from './IconButton';
import { Portal } from './Portal';

/**
 * Schublade für die fachlichen Seitenleisten (REQ-25 §1) — Nachfolger von
 * `SidebarShell`/`Sidebar` mit `e-sidebar`. Vorlage ist `Drawer.tsx` samt
 * `use-overlay.ts` der ChinaApp.
 */
export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Zweite Zeile im Kopf, z. B. „Datensatz 4711". */
  description?: string;
  /** CSS-Breite, z. B. '720px' oder '80rem'. Standard '32rem'. */
  width?: string;
  /** Standard 'end' (rechts). */
  side?: 'end' | 'start';
  footer?: ReactNode;
  children: ReactNode;
  /**
   * Standard `false`: Ein Klick auf die Abdunklung schließt nicht — wie bisher
   * (`CloseOnDocumentClick` war im Altclient nicht gesetzt), damit Eingaben nicht verloren gehen.
   */
  closeOnOverlayClick?: boolean;
  /** Element, das beim Öffnen den Fokus bekommt; sonst das erste Bedienelement im Rumpf. */
  initialFocusRef?: RefObject<HTMLElement | null>;
  id?: string;
  className?: string;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]';

/**
 * Offene Popups der Bedienelemente (Auswahlliste, Kalender, Menü). Sie hängen per Portal
 * am `body`; solange eines offen ist, gehört `Escape` ihm. Kurzhinweise tragen ebenfalls
 * `.e-popup`, reagieren aber nicht auf `Escape` und zählen deshalb nicht.
 */
const CONTROL_POPUP = '.e-popup:not(.e-tooltip-wrap), .e-dropdown-popup';

/**
 * Bedienbare Elemente der Schublade in Tab-Reihenfolge. Ausgeblendete Bereiche (etwa
 * inaktive Reiter) und `tabindex="-1"` zählen nicht — sonst läge das „letzte" Element der
 * Fokusfalle dort, wo Tab nie ankommt.
 */
function focusableIn(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (element) =>
      element.tabIndex >= 0 && element.closest('[hidden], [aria-hidden="true"]') === null,
  );
}

export function Drawer(props: DrawerProps) {
  const { initialFocusRef, onClose, open } = props;
  const width = props.width ?? '32rem';
  const side = props.side ?? 'end';
  const titleId = useId();
  const descriptionId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  // Fokus beim Öffnen setzen und beim Schließen an das auslösende Element zurückgeben.
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const body = bodyRef.current;
    const target =
      initialFocusRef?.current ??
      (body === null ? undefined : focusableIn(body)[0]) ??
      panelRef.current;
    target?.focus();

    return () => {
      previous?.focus();
    };
  }, [initialFocusRef, open]);

  if (!open) return null;

  // Der Handler sitzt am Panel und nicht am Dokument: Ein Dialog über der Schublade hält
  // `Escape` mit `stopPropagation` für sich. Tastendrücke aus Portalen (Dialog, Popup)
  // laufen im React-Baum zwar hier vorbei, gehören aber ihnen.
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const panel = event.currentTarget;
    if (!(event.target instanceof Node) || !panel.contains(event.target)) return;

    if (event.key === 'Escape') {
      // Das Bedienelement schließt sein Popup selbst; die Schublade bleibt offen.
      if (document.querySelector(CONTROL_POPUP) !== null) return;
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== 'Tab') return;

    // Fokusfalle wie in `Dialog.tsx`: Tab bleibt innerhalb der Schublade.
    const nodes = focusableIn(panel);
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (first === undefined || last === undefined) {
      event.preventDefault();
      return;
    }
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === panel)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <Portal>
      <div
        className="iwm-drawer-backdrop"
        onClick={props.closeOnOverlayClick === true ? onClose : undefined}
      />
      <div
        id={props.id}
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={props.description === undefined ? undefined : descriptionId}
        tabIndex={-1}
        style={{ width }}
        className={cx(
          'iwm-drawer',
          side === 'start' ? 'iwm-drawer--start' : 'iwm-drawer--end',
          props.className,
        )}
        onKeyDown={onKeyDown}
      >
        <header className="iwm-drawer-header">
          <div className="iwm-drawer-heading">
            <h2 id={titleId} className="iwm-drawer-title">
              {props.title}
            </h2>
            {props.description === undefined ? null : (
              <p id={descriptionId} className="iwm-drawer-description">
                {props.description}
              </p>
            )}
          </div>
          <IconButton
            iconClass="fa-solid fa-xmark"
            title="Schließen"
            className="iwm-drawer-close"
            onClick={onClose}
          />
        </header>
        <div ref={bodyRef} className="iwm-drawer-body">
          {props.children}
        </div>
        {props.footer === undefined ? null : (
          <footer className="iwm-drawer-footer">{props.footer}</footer>
        )}
      </div>
    </Portal>
  );
}

export default Drawer;
